import { useState, useEffect, useCallback, useMemo } from 'react';
import { getAdjustors } from '../api/insurance';
import type { InsuranceAdjustor } from '../types';

interface UseAdjustorsOptions {
  isActive?: boolean;
}

/**
 * Hook for fetching adjustors for a specific insurance company
 */
export function useAdjustors(companyId: number | undefined, options?: UseAdjustorsOptions) {
  const [data, setData] = useState<InsuranceAdjustor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize options to avoid unnecessary re-renders
  const optionsKey = useMemo(() => JSON.stringify(options), [options]);

  const fetchAdjustors = useCallback(async () => {
    if (!companyId) {
      setData([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const adjustors = await getAdjustors(companyId, options?.isActive);
      setData(adjustors);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch adjustors');
    } finally {
      setIsLoading(false);
    }
  }, [companyId, options?.isActive]);

  useEffect(() => {
    fetchAdjustors();
  }, [companyId, optionsKey, fetchAdjustors]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchAdjustors,
  };
}
